import { CbhIcon, Slider, Text, Title } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, Fade, IconButton, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import pluralize from "pluralize";
import { useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import { TabRouterPath } from "../../routing/constant/tabRoute";
import { AVERAGE_SHIFTS_PER_MONTH, savingsAfter, weeksUntilGoal } from "../savingsService";
import { useWorkerSavingsData } from "../useWorkerSavingsData";

interface FormData {
  savingsAmountPerShift: number;
}

const MIN_AMOUNT = 0;
const MAX_AMOUNT = 50;

export function SavingsConfigurationPortioningPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const worker = useDefinedWorker();
  const { workerSavingsData, setWorkerSavingsData } = useWorkerSavingsData();
  const history = useHistory();
  const form = useForm<FormData>({
    defaultValues: {
      savingsAmountPerShift: workerSavingsData.savingsAmountPerShift ?? 0,
    },
  });

  const defaultShiftsPerMonth = Math.round(AVERAGE_SHIFTS_PER_MONTH);
  const [customShiftsPerMonth, setCustomShiftsPerMonth] = useState(
    workerSavingsData.shiftsPerMonth || defaultShiftsPerMonth
  );

  const handleIncrementShifts = () => {
    setCustomShiftsPerMonth((prev) => prev + 1);
  };

  const handleDecrementShifts = () => {
    setCustomShiftsPerMonth((prev) => Math.max(1, prev - 1));
  };

  const onSubmit = (data: FormData) => {
    const newSavingsData = {
      ...workerSavingsData,
      savingsAmountPerShift: data.savingsAmountPerShift,
      shiftsPerMonth: customShiftsPerMonth,
      savingsOnboardingCompleted: true,
    };

    setWorkerSavingsData(newSavingsData);

    logEvent(APP_V2_APP_EVENTS.SAVINGS_CONFIGURATION_CREATED, {
      workerId: worker.userId,
      savingsData: newSavingsData,
    });

    history.replace(TabRouterPath.SAVINGS_BOOK_CALL);
  };

  const savingsAmountPerShift = form.watch("savingsAmountPerShift");

  return (
    <ThemeProvider theme={getTheme()}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <PageWrapper variant="tertiary">
            <StickyScrollPageHeader
              title="Savings Plan"
              scrollContainerRef={scrollRef}
              fallbackGoBackPath={TabRouterPath.SAVINGS_CONFIGURATION_GOALS}
              headerVariant="tertiary"
            />

            <PageContent ref={scrollRef}>
              <Stack spacing={10} width="100%">
                <Title component="h4" variant="h4">
                  How much do you want to save per shift?
                </Title>

                <Controller
                  name="savingsAmountPerShift"
                  control={form.control}
                  render={({ field }) => {
                    const weeksPerMonth = 4.345;
                    const shiftsPerWeek = customShiftsPerMonth / weeksPerMonth;
                    const savingsPerWeek = field.value * shiftsPerWeek;

                    // Calculate yearly savings using compound interest over 52 weeks
                    const { currentSavings: yearlySavings } = savingsAfter(
                      0, // Start from 0 to get just the yearly amount
                      savingsPerWeek,
                      52
                    );

                    const weeksNeeded = weeksUntilGoal(
                      workerSavingsData.savingsAmount,
                      savingsPerWeek,
                      workerSavingsData.savingsGoal
                    );

                    return (
                      <Stack spacing={12} sx={{ width: "100%" }}>
                        <Stack spacing={4} sx={{ paddingX: 2 }}>
                          <Box sx={{ textAlign: "center" }}>
                            <Text
                              variant="h3"
                              sx={(theme) => ({
                                fontSize: "3rem",
                                fontWeight: "bold",
                                color: theme.palette.primary.main,
                              })}
                            >
                              ${field.value}
                            </Text>
                            <Text
                              variant="body2"
                              sx={(theme) => ({ color: theme.palette.text.secondary })}
                            >
                              per shift
                            </Text>
                          </Box>
                          <Slider
                            thumb
                            value={field.value}
                            onChange={(_, value) => field.onChange(value)}
                            min={MIN_AMOUNT}
                            max={MAX_AMOUNT}
                            step={1}
                            marks={[
                              {
                                value: MIN_AMOUNT,
                                label: <span style={{ fontSize: "1.2em" }}>${MIN_AMOUNT}</span>,
                              },
                              {
                                value: MAX_AMOUNT,
                                label: <span style={{ fontSize: "1.2em" }}>${MAX_AMOUNT}</span>,
                              },
                            ]}
                            sx={{
                              "& .MuiSlider-thumb": {
                                transition: "left 0.3s ease-out",
                              },
                              "& .MuiSlider-track": {
                                transition: "width 0.3s ease-out",
                              },
                              "& .MuiSlider-markLabel": {
                                marginTop: "-1.25rem",
                              },
                            }}
                          />
                        </Stack>

                        <Fade in={field.value > 0} timeout={500}>
                          <Box>
                            <Box sx={{ textAlign: "center", paddingX: 2 }}>
                              <Text
                                variant="h5"
                                sx={(theme) => ({
                                  color: theme.palette.text.primary,
                                })}
                              >
                                If you work{" "}
                                <strong>
                                  {customShiftsPerMonth} {pluralize("shift", customShiftsPerMonth)}
                                </strong>{" "}
                                per month, you'll save{" "}
                                <strong>${Math.round(yearlySavings)} </strong> each year! You'll
                                reach your goal after{" "}
                                <strong>
                                  {weeksNeeded} {pluralize("week", weeksNeeded)}
                                </strong>
                              </Text>
                            </Box>
                            <Box sx={{ textAlign: "center", paddingX: 2, paddingTop: 4 }}></Box>
                            <Box style={{ paddingTop: "48px" }}>
                              <Text
                                variant="h6"
                                sx={(theme) => ({
                                  color: theme.palette.text.primary,
                                })}
                              >
                                You could reach your savings goals faster if you work more often:
                              </Text>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                paddingX: 2,
                                paddingTop: 4,
                                gap: 2,
                              }}
                            >
                              <IconButton
                                onClick={handleDecrementShifts}
                                disabled={customShiftsPerMonth <= 1}
                                sx={(theme) => ({
                                  backgroundColor: theme.palette.background.paper,
                                  border: `1px solid ${theme.palette.divider}`,
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                  "&:disabled": {
                                    opacity: 0.5,
                                  },
                                })}
                              >
                                <CbhIcon type="minus" size="medium" />
                              </IconButton>

                              <Text
                                variant="h6"
                                sx={(theme) => ({
                                  minWidth: "3rem",
                                  textAlign: "center",
                                  color: theme.palette.text.primary,
                                })}
                              >
                                {`${customShiftsPerMonth} ${pluralize(
                                  "shift",
                                  customShiftsPerMonth
                                )} per month`}
                              </Text>

                              <IconButton
                                onClick={handleIncrementShifts}
                                sx={(theme) => ({
                                  backgroundColor: theme.palette.background.paper,
                                  border: `1px solid ${theme.palette.divider}`,
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                })}
                              >
                                <CbhIcon type="plus" size="medium" />
                              </IconButton>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                marginBottom: 2,
                              }}
                              style={{ marginTop: "4px" }}
                            >
                              <CbhIcon type="info" size="small" style={{ marginTop: "1px" }} />
                              <Text
                                variant="body1"
                                sx={(theme) => ({
                                  color: theme.palette.text.secondary,
                                })}
                              >
                                You work an average of{" "}
                                <strong>
                                  {defaultShiftsPerMonth}{" "}
                                  {pluralize("shift", defaultShiftsPerMonth)}
                                </strong>{" "}
                                per month
                              </Text>
                            </Box>
                          </Box>
                        </Fade>
                      </Stack>
                    );
                  }}
                />
              </Stack>
            </PageContent>
            <PageFooterWrapper>
              <Button disabled={savingsAmountPerShift === 0} fullWidth type="submit">
                Submit
              </Button>
            </PageFooterWrapper>
          </PageWrapper>
        </FormProvider>
      </form>
    </ThemeProvider>
  );
}
